import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Check, ChevronLeft, ChevronRight, ClipboardCheck, Eye,
  FileSearch, GitCompare, HelpCircle, History as HistoryIcon, ListMusic,
  Loader2, Pencil, Play, Plus, RefreshCw, RotateCcw, Send, Target, X,
} from "lucide-react";

import { api, errMsg } from "@/lib/api";
import {
  canAccessBriefIntelligence, canWrite, useAuth,
} from "@/lib/auth";
import EmptyState, { PageHeader } from "@/components/Common";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

const NOT_CONFIGURED_MESSAGE = "Automated structured extraction is not currently configured. The brief and deterministic source information remain available for human review.";

function formatDate(value) {
  if (!value) return "\u2014";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "\u2014" : date.toLocaleString();
}

function formatBytes(value) {
  if (!Number.isFinite(value) || value < 0) return "\u2014";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

// Correction 2 — string-safe error handling.
function safeErrorText(error, fallback) {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (
    detail
    && typeof detail === "object"
    && !Array.isArray(detail)
    && typeof detail.message === "string"
    && detail.message.trim()
  ) {
    return detail.message;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg)
      .filter((message) => typeof message === "string" && message.trim());

    if (messages.length) {
      return messages.join(" ");
    }
  }

  const resolved = errMsg(error, fallback);
  return typeof resolved === "string" && resolved.trim()
    ? resolved
    : fallback;
}

// Correction 7 — minimise detail data before state.
function projectBriefDetail(data) {
  const source = data?.source || {};
  return {
    id: data?.id || "",
    ref_id: data?.ref_id || "",
    title: data?.title || "",
    workflow_status: data?.workflow_status || "",
    source: {
      source_kind: source.source_kind || "",
    },
    created_at: data?.created_at || "",
    updated_at: data?.updated_at || "",
    created_by_email: data?.created_by_email || "",
  };
}

function projectSourceInfo(data) {
  return {
    filename: data?.filename || "",
    content_type: data?.content_type || "",
    source_kind: data?.source_kind || "",
    size_bytes: Number.isFinite(data?.size_bytes)
      ? data.size_bytes
      : null,
    checksum_sha256: data?.checksum_sha256 || "",
    source_text_preview: data?.source_text_preview || "",
    uploaded_at: data?.uploaded_at || "",
    uploaded_by_email: data?.uploaded_by_email || "",
  };
}

function projectExtraction(data) {
  if (!data || typeof data !== "object" || !Object.keys(data).length) {
    return null;
  }
  return {
    id: data.id || "",
    brief_id: data.brief_id || "",
    version_no:
      data.version_no === null || data.version_no === undefined
        ? null
        : data.version_no,
    status: data.status || "",
    extraction_method: data.extraction_method || "",
    provider_name: data.provider_name || "",
    provider_model: data.provider_model || "",
    created_at: data.created_at || "",
    started_at: data.started_at || "",
    completed_at: data.completed_at || "",
    error_safe: data.error_safe || "",
    // Assignment 2D additions — bounded projections of the accepted schema.
    warnings: Array.isArray(data.warnings) ? data.warnings.slice(0, 100) : [],
    missing_fields: Array.isArray(data.missing_fields)
      ? data.missing_fields.slice(0, 300)
      : [],
    structured_data: data.structured_data && typeof data.structured_data === "object"
      ? projectStructuredData(data.structured_data)
      : null,
  };
}

// Reduce the accepted BriefStructuredData object into the exact list of
// Assignment 2D approved display fields. This intentionally forgets any
// non-approved schema branch (e.g. field_annotations) so it can never leak
// into the browser.

// Assignment 2H — read-only walk of a dotted canonical path over the raw
// structured payload. Returns undefined when no concrete value exists.
function walkCanonicalPath(raw, path) {
  let node = raw;
  for (const part of String(path).split(".")) {
    if (
      !node || typeof node !== "object" || Array.isArray(node)
      || !(part in node)
    ) {
      return undefined;
    }
    node = node[part];
  }
  return node;
}

// Assignment 2H — bounded, display-safe projection of one machine value.
function boundCanonicalValue(value) {
  if (value === undefined || value === null) {
    return { kind: "empty", display: "", raw: null };
  }
  if (Array.isArray(value)) {
    const items = value.slice(0, 20).map((item) => String(item).slice(0, 200));
    return { kind: "array", display: items.join(", "), raw: items };
  }
  if (typeof value === "boolean") {
    return { kind: "boolean", display: value ? "True" : "False", raw: value };
  }
  if (typeof value === "number") {
    return { kind: "number", display: String(value), raw: value };
  }
  const text = String(value).slice(0, 500);
  return { kind: "string", display: text, raw: text };
}

function projectStructuredData(raw) {
  const source = raw.source_information || {};
  const opportunity = raw.opportunity_details || {};
  const music = raw.music_requirements || {};
  const rights = raw.rights_and_clearance || {};
  const financial = raw.financial || {};
  const deadlines = raw.deadlines || {};
  const delivery = raw.delivery || {};
  // Assignment 2G — bounded projection of field_annotations. Keys are
  // dotted "section.field" paths. Only whitelisted fields on each
  // annotation are exposed to the browser. Assignment 2G-R made this
  // rule explicit: character_start / character_end are ALWAYS dropped
  // here and MUST NEVER be rendered by any downstream component.
  // Server-side records retain them; the operator-facing UI does not.
  const rawAnns = (raw.field_annotations && typeof raw.field_annotations === "object")
    ? raw.field_annotations : {};
  const field_annotations = {};
  for (const path of Object.keys(rawAnns).slice(0, 500)) {
    const a = rawAnns[path];
    if (!a || typeof a !== "object") continue;
    const evidenceIn = Array.isArray(a.evidence) ? a.evidence.slice(0, 3) : [];
    const evidence = [];
    for (const e of evidenceIn) {
      if (!e || typeof e !== "object") continue;
      if (typeof e.source_excerpt !== "string" || !e.source_excerpt) continue;
      // 2G-R: only these three keys reach the DOM. character_start /
      // character_end are intentionally NOT copied. Do not add them.
      evidence.push({
        source_excerpt: String(e.source_excerpt).slice(0, 500),
        source_section: typeof e.source_section === "string"
          ? String(e.source_section).slice(0, 200) : "",
        source_page: (typeof e.source_page === "number" && e.source_page > 0)
          ? e.source_page : null,
      });
    }
    field_annotations[path] = {
      confidence: typeof a.confidence === "string" ? a.confidence : "",
      human_review_status: typeof a.human_review_status === "string"
        ? a.human_review_status : "Unreviewed",
      extraction_method: typeof a.extraction_method === "string"
        ? a.extraction_method : "",
      evidence,
    };
  }

  // Assignment 2H — bounded machine values for every grounded path so the
  // adjudication panel can show what the extractor originally produced.
  const canonical_values = {};
  for (const path of Object.keys(field_annotations)) {
    canonical_values[path] = boundCanonicalValue(walkCanonicalPath(raw, path));
  }

  const dedupe = (list) => {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    const out = [];
    for (const item of list) {
      if (typeof item !== "string") continue;
      const trimmed = item.trim();
      if (!trimmed || seen.has(trimmed.toLowerCase())) continue;
      seen.add(trimmed.toLowerCase());
      out.push(trimmed);
      if (out.length >= 100) break;
    }
    return out;
  };

  const contactBits = Array.isArray(delivery.contact_information)
    ? delivery.contact_information
    : [];
  // Correction Pass 1 — prefer the dedicated fields; fall back to parsing
  // the legacy contact_information list for records persisted before the
  // schema addition.
  const contactName = (() => {
    if (typeof delivery.contact_name === "string" && delivery.contact_name.trim()) {
      return delivery.contact_name.trim();
    }
    for (const bit of contactBits) {
      if (typeof bit === "string" && bit.toLowerCase().startsWith("contact:")) {
        return bit.slice(bit.indexOf(":") + 1).trim();
      }
    }
    return "";
  })();
  const contactEmail = (() => {
    if (typeof delivery.contact_email === "string" && delivery.contact_email.trim()) {
      return delivery.contact_email.trim();
    }
    for (const bit of contactBits) {
      if (typeof bit === "string" && bit.toLowerCase().startsWith("email:")) {
        return bit.slice(bit.indexOf(":") + 1).trim();
      }
    }
    return "";
  })();

  const genres = [];
  if (music.primary_genre) genres.push(music.primary_genre);
  if (Array.isArray(music.secondary_genres)) {
    for (const g of music.secondary_genres) if (typeof g === "string" && g.trim()) genres.push(g.trim());
  }

  const vocalRequirements = music.vocal_preference
    || music.vocal_or_instrumental
    || "";

  const budgetDisplay = (() => {
    if (financial.stated_budget_text) return financial.stated_budget_text;
    if (financial.budget_minimum != null && financial.budget_maximum != null) {
      return `${financial.budget_minimum}\u2013${financial.budget_maximum}`;
    }
    if (financial.budget_minimum != null) return String(financial.budget_minimum);
    return "";
  })();

  const editRequirement = (() => {
    // Correction Pass 1 — prefer the preserved source wording; fall back
    // to the boolean summary for legacy records.
    if (
      typeof music.edit_or_cut_requirements_text === "string"
      && music.edit_or_cut_requirements_text.trim()
    ) {
      return music.edit_or_cut_requirements_text.trim();
    }
    if (music.cut_downs_required === true) return "Required";
    if (music.cut_downs_required === false) return "Not required";
    return "";
  })();

  return {
    field_annotations,
    canonical_values,
    core: {
      brief_title: source.brief_title || "",
      client_or_company: source.client_or_buyer_name || "",
      project_name: opportunity.project_name || "",
      brief_type: opportunity.project_type || "",
      // Correction Pass 2 — prefer the dedicated summary field; fall
      // back to legacy campaign_or_episode_information for records
      // persisted before the schema addition.
      summary: (
        (typeof opportunity.summary === "string" && opportunity.summary.trim())
          ? opportunity.summary
          : (opportunity.campaign_or_episode_information || "")
      ),
    },
    creative: {
      genres: dedupe(genres),
      moods: dedupe(music.moods),
      // Correction Pass 2 — prefer the dedicated themes field; fall
      // back to legacy lyrical_theme for records persisted before the
      // schema addition.
      themes: (
        (typeof music.themes === "string" && music.themes.trim())
          ? music.themes
          : (music.lyrical_theme || "")
      ),
      reference_artists: dedupe(music.similar_artists),
      reference_tracks: dedupe(music.similar_songs),
      instrumentation: dedupe(music.instrumentation),
      vocal_requirements: vocalRequirements,
      language_requirements: dedupe(music.languages),
      tempo_or_bpm: music.tempo_description || "",
      era_or_style: music.era || "",
    },
    usage: {
      media_type: opportunity.usage_type || "",
      usage_description: opportunity.placement_description || "",
      territory: dedupe(rights.territory),
      term: rights.licence_term || "",
      exclusivity: rights.exclusivity || "",
      edit_or_cut_requirements: editRequirement,
    },
    business: {
      budget: budgetDisplay,
      currency: financial.currency || "",
      submission_deadline: deadlines.submission_deadline || "",
      // Correction Pass 1 — prefer the dedicated project_timeline field;
      // fall back to legacy urgency mapping for records persisted before
      // the schema addition.
      project_timeline: (
        (typeof deadlines.project_timeline === "string" && deadlines.project_timeline.trim())
          ? deadlines.project_timeline
          : (deadlines.urgency || "")
      ),
      contact_name: contactName,
      contact_email: contactEmail,
    },
    restrictions: {
      lyrical_restrictions: music.explicit_content_restrictions || "",
      // Correction Pass 2 — prefer the dedicated content_restrictions
      // field; fall back to legacy conflict_restrictions for records
      // persisted before the schema addition.
      content_restrictions: (
        (typeof rights.content_restrictions === "string" && rights.content_restrictions.trim())
          ? rights.content_restrictions
          : (rights.conflict_restrictions || "")
      ),
      // Correction Pass 1 — prefer the dedicated rights_requirements
      // field; fall back to composing a display from adjacent rights
      // fields for legacy records.
      rights_requirements: (
        (typeof rights.rights_requirements === "string" && rights.rights_requirements.trim())
          ? rights.rights_requirements
          : ([
              rights.exclusivity,
              rights.licence_term,
              rights.master_ownership_requirement,
              rights.publishing_control_requirement,
            ].filter((value) => typeof value === "string" && value.trim()).join(" \u2022 ") || "")
      ),
      delivery_requirements: dedupe(delivery.special_submission_instructions),
      // Correction Pass 1 — prefer the dedicated additional_notes field;
      // fall back to the legacy naming_convention mapping.
      additional_notes: (
        (typeof delivery.additional_notes === "string" && delivery.additional_notes.trim())
          ? delivery.additional_notes
          : (delivery.naming_convention || "")
      ),
    },
  };
}

// Correction 8 — preserve zero values in read fields.
function Read({ label, value, mono, testid }) {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div
        className={`text-[13px] whitespace-pre-wrap break-words ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      >
        {isEmpty
          ? <span className="text-muted-foreground italic">—</span>
          : value}
      </div>
    </div>
  );
}

function NeutralStatus({ value, testid }) {
  return (
    <Badge
      variant="outline"
      className="text-[10px] uppercase tracking-[0.14em] font-semibold"
      data-testid={testid}
    >
      {value || "\u2014"}
    </Badge>
  );
}

function ExtractionSummary({ extraction, prefix = "latest" }) {
  if (!extraction) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <Read
        label="Extraction ID"
        value={extraction.id}
        mono
        testid={`${prefix}-extraction-id`}
      />
      <Read
        label="Version"
        value={extraction.version_no != null ? String(extraction.version_no) : ""}
        testid={`${prefix}-extraction-version`}
      />
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Status</div>
        <NeutralStatus value={extraction.status} testid={`${prefix}-extraction-status`} />
      </div>
      <Read
        label="Method"
        value={extraction.extraction_method}
        testid={`${prefix}-extraction-method`}
      />
      <Read
        label="Provider"
        value={extraction.provider_name}
        testid={`${prefix}-extraction-provider`}
      />
      <Read
        label="Provider model"
        value={extraction.provider_model}
        testid={`${prefix}-extraction-provider-model`}
      />
      <Read
        label="Created"
        value={formatDate(extraction.created_at)}
        testid={`${prefix}-extraction-created`}
      />
      <Read
        label="Started"
        value={formatDate(extraction.started_at)}
        testid={`${prefix}-extraction-started`}
      />
      <Read
        label="Completed"
        value={formatDate(extraction.completed_at)}
        testid={`${prefix}-extraction-completed`}
      />
    </div>
  );
}

// Assignment 2D — value renderers for the approved structured display.
function StringValue({ value, testid }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div
      className={`text-[13px] whitespace-pre-wrap break-words ${empty ? "text-muted-foreground italic" : ""}`}
      data-testid={testid}
    >
      {empty ? "—" : value}
    </div>
  );
}

function ArrayValue({ values, testid, itemPrefix }) {
  if (!Array.isArray(values) || values.length === 0) {
    return (
      <div
        className="text-[13px] text-muted-foreground italic"
        data-testid={testid}
      >
        —
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {values.map((item, index) => (
        <Badge
          key={`${itemPrefix}-${index}`}
          variant="outline"
          className="text-[11px]"
          data-testid={`${testid}-item-${index}`}
        >
          {item}
        </Badge>
      ))}
    </div>
  );
}

function FieldRow({ label, children, testid }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
        data-testid={testid ? `${testid}-label` : undefined}
      >
        {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function FieldEvidence({ annotation, testid }) {
  if (!annotation || !Array.isArray(annotation.evidence) || annotation.evidence.length === 0) {
    return null;
  }
  return (
    <div
      className="mt-2 rounded-md border border-dashed border-border/70 bg-muted/30 px-2.5 py-2 text-[11px] leading-relaxed text-muted-foreground space-y-1.5"
      data-testid={testid}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className="inline-flex items-center rounded-full border border-border/60 bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground/80"
          data-testid={`${testid}-confidence`}
        >
          {annotation.confidence || "—"}
        </span>
        <span
          className="inline-flex items-center rounded-full border border-border/60 bg-background px-2 py-0.5 text-[10px] uppercase tracking-wider text-foreground/70"
          data-testid={`${testid}-review-status`}
        >
          {annotation.human_review_status || "Unreviewed"}
        </span>
      </div>
      {annotation.evidence.map((ev, idx) => (
        <div key={idx} className="space-y-0.5" data-testid={`${testid}-item-${idx}`}>
          <div className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wider text-foreground/60">
            <span>Excerpt</span>
            {ev.source_section ? <span>· {ev.source_section}</span> : null}
            {ev.source_page ? <span data-testid={`${testid}-item-${idx}-page`}>· page {ev.source_page}</span> : null}
          </div>
          <blockquote
            className="border-l-2 border-border/60 pl-2 italic text-foreground/85 whitespace-pre-wrap break-words"
            data-testid={`${testid}-item-${idx}-excerpt`}
          >
            {ev.source_excerpt}
          </blockquote>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assignment 2H — Reviewer field adjudication (human review layer).
// Adjudication is stored server-side in a SEPARATE collection; the machine
// extraction itself is never mutated. Everything below is display + control
// logic for the four reviewer actions.
// ---------------------------------------------------------------------------
const REVIEW_SECTION_LABELS = {
  source_information: "Source Information",
  opportunity_details: "Opportunity",
  music_requirements: "Music Requirements",
  rights_and_clearance: "Rights & Clearance",
  financial: "Financial",
  deadlines: "Deadlines",
  delivery: "Delivery",
};

function reviewFieldLabel(path) {
  const parts = String(path).split(".");
  const section = REVIEW_SECTION_LABELS[parts[0]] || parts[0];
  const field = (parts[1] || "")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
  return `${section} \u00b7 ${field}`;
}

const REVIEW_STATE_BADGE_CLASSES = {
  Unreviewed: "border-border/70 text-muted-foreground",
  Confirmed: "border-emerald-700/40 text-emerald-700 dark:text-emerald-400",
  Corrected: "border-amber-700/40 text-amber-700 dark:text-amber-400",
  Rejected: "border-rose-700/40 text-rose-700 dark:text-rose-400",
  "Needs Information": "border-sky-700/40 text-sky-700 dark:text-sky-400",
};

function projectFieldReview(row) {
  if (!row || typeof row !== "object") return null;
  return {
    field_path: typeof row.field_path === "string" ? row.field_path : "",
    review_status: typeof row.review_status === "string"
      ? row.review_status : "Unreviewed",
    corrected_value: row.corrected_value === undefined
      ? null : row.corrected_value,
    reviewer_note: typeof row.reviewer_note === "string"
      ? row.reviewer_note.slice(0, 500) : "",
    reviewed_by_email: typeof row.reviewed_by_email === "string"
      ? row.reviewed_by_email : "",
    reviewed_at: typeof row.reviewed_at === "string" ? row.reviewed_at : "",
  };
}

// Display-only merge: overlays the reviewer's adjudicated status onto the
// machine annotations so inline badges reflect human review. The underlying
// extraction data in state is never modified.
function mergeAnnotationsWithReviews(annotations, reviews) {
  const source = annotations && typeof annotations === "object"
    ? annotations : {};
  const merged = {};
  for (const path of Object.keys(source)) {
    const review = reviews ? reviews[path] : null;
    merged[path] = review && review.review_status
      ? { ...source[path], human_review_status: review.review_status }
      : source[path];
  }
  return merged;
}

function correctedValueDisplay(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  if (typeof value === "boolean") return value ? "True" : "False";
  return String(value);
}

// ---------------------------------------------------------------------------
// Assignment 2I — Source Viewer (verification only).
// The server resolves the stored evidence offsets and returns the parsed
// text pre-split into prefix/evidence/suffix — character offsets are never
// present in the browser. Viewing never mutates extraction or review state.
// ---------------------------------------------------------------------------
const SOURCE_TEXT_DISPLAY_BOUND = 120000;

function projectSourceView(raw) {
  if (!raw || typeof raw !== "object") return null;
  const text = (value) => (typeof value === "string" ? value : "");
  return {
    verification_status: text(raw.verification_status) || "source_unavailable",
    field_path: text(raw.field_path),
    confidence: text(raw.confidence),
    extracted_value: text(raw.extracted_value).slice(0, 500),
    source_excerpt: text(raw.source_excerpt).slice(0, 500),
    source_filename: text(raw.source_filename).slice(0, 200),
    prefix: text(raw.prefix).slice(0, SOURCE_TEXT_DISPLAY_BOUND),
    evidence: text(raw.evidence).slice(0, SOURCE_TEXT_DISPLAY_BOUND),
    suffix: text(raw.suffix).slice(0, SOURCE_TEXT_DISPLAY_BOUND),
    full_text: text(raw.full_text).slice(0, SOURCE_TEXT_DISPLAY_BOUND),
  };
}

const SOURCE_VIEW_STATUS_MESSAGES = {
  location_unavailable:
    "Exact source location unavailable. The full parsed source is shown below without a highlight.",
  location_mismatch:
    "Source verification unavailable for this field — the stored location no longer matches the stored excerpt. The full parsed source is shown below without a highlight.",
  source_unavailable:
    "Source verification unavailable — the parsed source text for this brief could not be loaded. Existing extraction and review data are unaffected.",
};

function SourceViewerSheet({
  open, onClose, loading, error, view, fieldLabel,
  onPrev, onNext, hasNav,
}) {
  const highlightRef = React.useRef(null);
  React.useEffect(() => {
    if (open && view?.verification_status === "verified" && highlightRef.current) {
      // Centre the evidence span where the environment supports it (jsdom-safe).
      if (typeof highlightRef.current.scrollIntoView === "function") {
        highlightRef.current.scrollIntoView({ block: "center" });
      }
    }
  }, [open, view]);

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl flex flex-col gap-0 p-0"
        data-testid="source-viewer-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b space-y-2 text-left">
          <div className="flex items-center justify-between gap-2 pr-8">
            <SheetTitle
              className="text-sm flex items-center gap-2"
              data-testid="source-viewer-field-label"
            >
              <Eye className="w-4 h-4" /> Verify evidence — {fieldLabel}
            </SheetTitle>
            {hasNav && (
              <div className="flex items-center gap-1">
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={onPrev} disabled={loading}
                  data-testid="source-viewer-prev-btn"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Prev
                </Button>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={onNext} disabled={loading}
                  data-testid="source-viewer-next-btn"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
          <SheetDescription className="text-[11.5px]">
            Read-only verification of the stored parsed source. Viewing never
            changes extracted values, evidence, or review state.
          </SheetDescription>
          {view && !loading && (
            <div className="space-y-1" data-testid="source-viewer-meta">
              <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
                {view.confidence ? (
                  <span
                    className="inline-flex items-center rounded-full border border-border/60 bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground/70"
                    data-testid="source-viewer-confidence"
                  >
                    {view.confidence}
                  </span>
                ) : null}
                {view.source_filename ? (
                  <span className="text-muted-foreground" data-testid="source-viewer-filename">
                    {view.source_filename}
                  </span>
                ) : null}
              </div>
              <div className="text-[12px]" data-testid="source-viewer-extracted-value">
                <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mr-2">
                  Extracted value
                </span>
                <span className="break-words">{view.extracted_value || "—"}</span>
              </div>
              {view.source_excerpt ? (
                <div className="text-[11.5px] text-muted-foreground italic break-words" data-testid="source-viewer-excerpt">
                  Stored excerpt: “{view.source_excerpt}”
                </div>
              ) : null}
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="source-viewer-body">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="source-viewer-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading parsed source…
            </div>
          ) : error ? (
            <Alert data-testid="source-viewer-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : view ? (
            <div className="space-y-3">
              {view.verification_status !== "verified" && (
                <Alert data-testid="source-viewer-status">
                  <AlertDescription className="text-[12.5px]">
                    {SOURCE_VIEW_STATUS_MESSAGES[view.verification_status]
                      || SOURCE_VIEW_STATUS_MESSAGES.source_unavailable}
                  </AlertDescription>
                </Alert>
              )}
              {view.verification_status === "verified" ? (
                <div
                  className="rounded-md border bg-muted/20 p-4 text-[13px] leading-relaxed whitespace-pre-wrap break-words"
                  data-testid="source-viewer-text"
                >
                  {view.prefix}
                  <mark
                    ref={highlightRef}
                    className="rounded-[2px] bg-amber-400/40 px-0.5 text-inherit dark:bg-amber-500/30"
                    data-testid="source-viewer-highlight"
                  >
                    {view.evidence}
                  </mark>
                  {view.suffix}
                </div>
              ) : view.full_text ? (
                <div
                  className="rounded-md border bg-muted/20 p-4 text-[13px] leading-relaxed whitespace-pre-wrap break-words"
                  data-testid="source-viewer-text"
                >
                  {view.full_text}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="border-t px-5 py-3">
          <Button
            type="button" size="sm" variant="outline"
            onClick={onClose}
            data-testid="source-viewer-close-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to review
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ReviewStateBadge({ status, testid }) {
  const effective = status || "Unreviewed";
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-[0.12em] font-semibold ${REVIEW_STATE_BADGE_CLASSES[effective] || REVIEW_STATE_BADGE_CLASSES.Unreviewed}`}
      data-testid={testid}
    >
      {effective}
    </Badge>
  );
}

// Inline editor shown while the reviewer is entering a corrected value.
function CorrectedValueEditor({
  path, valueKind, draft, setDraft, busy, onSave, onCancel,
}) {
  const tid = `review-correct-editor-${path}`;
  return (
    <div
      className="mt-2 rounded-md border border-amber-700/30 bg-amber-500/5 p-2.5 space-y-2"
      data-testid={tid}
    >
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        Corrected value
      </div>
      {valueKind === "boolean" ? (
        <div className="flex gap-2">
          {["True", "False"].map((option) => (
            <Button
              key={option}
              type="button"
              size="sm"
              variant={draft === option ? "default" : "outline"}
              onClick={() => setDraft(option)}
              data-testid={`${tid}-bool-${option.toLowerCase()}`}
            >
              {option}
            </Button>
          ))}
        </div>
      ) : valueKind === "array" ? (
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={3}
          placeholder="One value per line"
          className="text-[13px]"
          data-testid={`${tid}-textarea`}
        />
      ) : (
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="text-[13px]"
          data-testid={`${tid}-input`}
        />
      )}
      {valueKind === "array" && (
        <div className="text-[10.5px] text-muted-foreground">
          Enter one value per line.
        </div>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={busy}
          data-testid={`${tid}-save`}
        >
          {busy
            ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            : <Check className="w-3.5 h-3.5 mr-1.5" />}
          Save correction
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={busy}
          data-testid={`${tid}-cancel`}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assignment 2J — Grounded History Diff (read-only comparison).
// The server aligns fields by canonical key, classifies change states, and
// auto-orders sides earlier -> later. Nothing here writes anything.
// ---------------------------------------------------------------------------
const DIFF_STATE_BADGE_CLASSES = {
  Unchanged: "border-border/70 text-muted-foreground",
  Changed: "border-amber-700/40 text-amber-700 dark:text-amber-400",
  Added: "border-emerald-700/40 text-emerald-700 dark:text-emerald-400",
  Removed: "border-rose-700/40 text-rose-700 dark:text-rose-400",
};

function projectDiffSide(raw) {
  const text = (value) => (typeof value === "string" ? value : "");
  return {
    id: text(raw?.id),
    version_no: typeof raw?.version_no === "number" ? raw.version_no : null,
    extraction_method: text(raw?.extraction_method),
    provider_name: text(raw?.provider_name),
    provider_model: text(raw?.provider_model),
    completed_at: text(raw?.completed_at),
  };
}

function projectDiffRow(raw) {
  const text = (value) => (typeof value === "string" ? value : "");
  const side = (s) => ({
    value: text(s?.value).slice(0, 500),
    has_value: Boolean(s?.has_value),
    confidence: text(s?.confidence),
    evidence_excerpt: text(s?.evidence_excerpt).slice(0, 300),
  });
  return {
    field_path: text(raw?.field_path),
    change_state: DIFF_STATE_BADGE_CLASSES[raw?.change_state]
      ? raw.change_state : "Unchanged",
    confidence_changed: Boolean(raw?.confidence_changed),
    evidence_changed: Boolean(raw?.evidence_changed),
    left: side(raw?.left),
    right: side(raw?.right),
  };
}

function projectHistoryDiff(raw) {
  if (!raw || typeof raw !== "object") return null;
  const summary = raw.summary || {};
  const count = (key) => (typeof summary[key] === "number" ? summary[key] : 0);
  return {
    left: projectDiffSide(raw.left),
    right: projectDiffSide(raw.right),
    summary: {
      unchanged: count("unchanged"),
      changed: count("changed"),
      added: count("added"),
      removed: count("removed"),
      total: count("total"),
    },
    rows: Array.isArray(raw.rows)
      ? raw.rows.map(projectDiffRow).filter((row) => row.field_path)
      : [],
  };
}

function DiffSideCell({ side, path, prefix, extractionId, onViewSource }) {
  return (
    <div className="space-y-1 min-w-0" data-testid={`${prefix}-${path}`}>
      {side.has_value ? (
        <div className="text-[12px] whitespace-pre-wrap break-words">
          {side.value || <span className="italic text-muted-foreground">(empty)</span>}
        </div>
      ) : (
        <div className="text-[12px] italic text-muted-foreground">— no value —</div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {side.confidence ? (
          <span className="inline-flex items-center rounded-full border border-border/60 bg-background px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-foreground/70">
            {side.confidence}
          </span>
        ) : (
          <span className="text-[10px] italic text-muted-foreground">no confidence</span>
        )}
        {side.evidence_excerpt && extractionId && onViewSource ? (
          <Button
            type="button" size="sm" variant="ghost"
            className="h-5 px-1.5 text-[10px] text-muted-foreground"
            onClick={() => onViewSource(path, extractionId)}
            data-testid={`${prefix}-${path}-view-source`}
          >
            <Eye className="w-3 h-3 mr-1" /> Source
          </Button>
        ) : null}
      </div>
      {side.evidence_excerpt ? (
        <blockquote className="border-l-2 border-border/60 pl-1.5 text-[10.5px] italic text-muted-foreground whitespace-pre-wrap break-words">
          {side.evidence_excerpt}
        </blockquote>
      ) : (
        <div className="text-[10px] italic text-muted-foreground/70">no evidence excerpt</div>
      )}
    </div>
  );
}

function DiffSideHeading({ label, side, testid }) {
  return (
    <div className="rounded-md border bg-muted/20 p-2.5 min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </div>
      <div className="text-[12.5px] font-semibold">
        Version {side.version_no ?? "—"}
        {side.extraction_method ? ` · ${side.extraction_method}` : ""}
      </div>
      <div className="text-[11px] text-muted-foreground break-words">
        {[side.provider_name, side.provider_model].filter(Boolean).join(" · ") || "Provider not recorded"}
      </div>
      <div className="text-[11px] text-muted-foreground">
        {side.completed_at ? `Completed ${formatDate(side.completed_at)}` : "Completed time not recorded"}
      </div>
    </div>
  );
}

function HistoryDiffDialog({ open, onClose, loading, error, diff, filter, setFilter, onViewSource }) {
  const rows = diff
    ? diff.rows.filter((row) => filter === "all" || row.change_state !== "Unchanged")
    : [];
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        className="max-w-5xl w-[96vw] max-h-[85vh] flex flex-col gap-3"
        data-testid="history-diff-dialog"
      >
        <DialogHeader className="space-y-1.5">
          <DialogTitle className="text-sm flex items-center gap-2">
            <GitCompare className="w-4 h-4" /> Grounded history diff
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Read-only comparison of two extraction versions. Nothing is
            re-run, edited, or overwritten by comparing.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8" data-testid="history-diff-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Comparing versions…
          </div>
        ) : error ? (
          <Alert data-testid="history-diff-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : diff ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <DiffSideHeading label="Earlier extraction" side={diff.left} testid="history-diff-left-meta" />
              <DiffSideHeading label="Later extraction" side={diff.right} testid="history-diff-right-meta" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-1.5" data-testid="history-diff-summary">
                {[
                  ["Unchanged", diff.summary.unchanged],
                  ["Changed", diff.summary.changed],
                  ["Added", diff.summary.added],
                  ["Removed", diff.summary.removed],
                ].map(([state, count]) => (
                  <Badge
                    key={state}
                    variant="outline"
                    className={`text-[10px] font-semibold ${DIFF_STATE_BADGE_CLASSES[state]}`}
                    data-testid={`history-diff-summary-${state.toLowerCase()}`}
                  >
                    {state}: {count}
                  </Badge>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Button
                  type="button" size="sm"
                  variant={filter === "all" ? "default" : "outline"}
                  onClick={() => setFilter("all")}
                  data-testid="history-diff-filter-all"
                >
                  All
                </Button>
                <Button
                  type="button" size="sm"
                  variant={filter === "changed" ? "default" : "outline"}
                  onClick={() => setFilter("changed")}
                  data-testid="history-diff-filter-changed"
                >
                  Changed only
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto rounded-md border" data-testid="history-diff-rows">
              {rows.length === 0 ? (
                <div className="p-4 text-[12.5px] italic text-muted-foreground" data-testid="history-diff-no-rows">
                  No fields match the current filter.
                </div>
              ) : (
                <ul className="divide-y">
                  {rows.map((row) => (
                    <li
                      key={row.field_path}
                      className="p-3 space-y-1.5"
                      data-testid={`history-diff-row-${row.field_path}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[12px] font-semibold text-foreground/90">
                          {reviewFieldLabel(row.field_path)}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-[9.5px] uppercase tracking-[0.12em] font-semibold ${DIFF_STATE_BADGE_CLASSES[row.change_state]}`}
                          data-testid={`history-diff-state-${row.field_path}`}
                        >
                          {row.change_state}
                        </Badge>
                        {row.confidence_changed && (
                          <span
                            className="text-[9.5px] uppercase tracking-wider text-sky-700 dark:text-sky-400 font-semibold"
                            data-testid={`history-diff-confidence-flag-${row.field_path}`}
                          >
                            confidence differs
                          </span>
                        )}
                        {row.evidence_changed && (
                          <span
                            className="text-[9.5px] uppercase tracking-wider text-sky-700 dark:text-sky-400 font-semibold"
                            data-testid={`history-diff-evidence-flag-${row.field_path}`}
                          >
                            evidence differs
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <DiffSideCell
                          side={row.left}
                          path={row.field_path}
                          prefix="history-diff-left"
                          extractionId={diff.left.id}
                          onViewSource={onViewSource}
                        />
                        <DiffSideCell
                          side={row.right}
                          path={row.field_path}
                          prefix="history-diff-right"
                          extractionId={diff.right.id}
                          onViewSource={onViewSource}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function FieldReviewRow({
  path, annotation, canonicalValue, review, writer, busyPath,
  editingPath, editorDraft, setEditorDraft, onAction, onStartCorrect,
  onSaveCorrect, onCancelCorrect, onViewSource,
  noteDraft, onNoteDraftChange, onSaveNote,
}) {
  const status = review?.review_status || "Unreviewed";
  const busy = busyPath === path;
  const editing = editingPath === path;
  const excerpt = annotation?.evidence?.[0]?.source_excerpt || "";
  // 2L — the standalone "Save note" action appears only when the field is
  // already adjudicated and the draft differs from the saved note. A note
  // alone never adjudicates an Unreviewed field.
  const draft = typeof noteDraft === "string" ? noteDraft : "";
  const savedNote = review?.reviewer_note || "";
  const noteDirty = draft.trim() !== savedNote;
  return (
    <li className="p-3 space-y-1.5" data-testid={`review-row-${path}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-semibold text-foreground/90">
          {reviewFieldLabel(path)}
        </span>
        {annotation?.confidence ? (
          <span
            className="inline-flex items-center rounded-full border border-border/60 bg-background px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground/70"
            data-testid={`review-confidence-${path}`}
          >
            {annotation.confidence}
          </span>
        ) : null}
        <ReviewStateBadge status={status} testid={`review-status-${path}`} />
        {excerpt ? (
          <Button
            type="button" size="sm" variant="ghost"
            className="h-6 px-2 text-[11px] text-muted-foreground"
            onClick={() => onViewSource(path)}
            data-testid={`review-view-source-btn-${path}`}
          >
            <Eye className="w-3.5 h-3.5 mr-1" /> View source
          </Button>
        ) : null}
      </div>

      <div className="text-[12.5px]">
        <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mr-2">
          Extracted value
        </span>
        <span
          className={`whitespace-pre-wrap break-words ${status === "Corrected" || status === "Rejected" ? "line-through decoration-border text-muted-foreground" : ""}`}
          data-testid={`review-extracted-value-${path}`}
        >
          {canonicalValue?.display
            ? canonicalValue.display
            : <span className="italic text-muted-foreground">—</span>}
        </span>
      </div>

      {status === "Corrected" && review?.corrected_value !== null && !editing && (
        <div
          className="text-[12.5px] rounded-md border border-amber-700/30 bg-amber-500/5 px-2 py-1.5"
          data-testid={`review-corrected-value-${path}`}
        >
          <span className="text-[10px] uppercase tracking-[0.16em] text-amber-700 dark:text-amber-400 mr-2 font-semibold">
            Reviewed / corrected value
          </span>
          <span className="whitespace-pre-wrap break-words">
            {correctedValueDisplay(review.corrected_value)}
          </span>
        </div>
      )}

      {excerpt ? (
        <blockquote
          className="border-l-2 border-border/60 pl-2 text-[11px] italic text-muted-foreground whitespace-pre-wrap break-words"
          data-testid={`review-evidence-${path}`}
        >
          {excerpt}
        </blockquote>
      ) : null}

      {editing && (
        <CorrectedValueEditor
          path={path}
          valueKind={canonicalValue?.kind || "string"}
          draft={editorDraft}
          setDraft={setEditorDraft}
          busy={busy}
          onSave={() => onSaveCorrect(path)}
          onCancel={onCancelCorrect}
        />
      )}

      {writer && !editing && (
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          <Button
            type="button" size="sm" variant="outline"
            disabled={busy}
            onClick={() => onAction(path, "Confirmed")}
            data-testid={`review-confirm-btn-${path}`}
          >
            <Check className="w-3.5 h-3.5 mr-1" /> Confirm
          </Button>
          <Button
            type="button" size="sm" variant="outline"
            disabled={busy}
            onClick={() => onStartCorrect(path)}
            data-testid={`review-correct-btn-${path}`}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" /> Correct
          </Button>
          <Button
            type="button" size="sm" variant="outline"
            disabled={busy}
            onClick={() => onAction(path, "Rejected")}
            data-testid={`review-reject-btn-${path}`}
          >
            <X className="w-3.5 h-3.5 mr-1" /> Reject
          </Button>
          <Button
            type="button" size="sm" variant="outline"
            disabled={busy}
            onClick={() => onAction(path, "Needs Information")}
            data-testid={`review-needs-info-btn-${path}`}
          >
            <HelpCircle className="w-3.5 h-3.5 mr-1" /> Needs Info
          </Button>
          {status !== "Unreviewed" && (
            <Button
              type="button" size="sm" variant="ghost"
              disabled={busy}
              onClick={() => onAction(path, "Unreviewed")}
              data-testid={`review-clear-btn-${path}`}
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          )}
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
      )}

      {/* 2L — optional reviewer note. Plain text, part of the human review
          record only; sent with every adjudication action. */}
      {writer && (
        <div className="pt-1 space-y-1" data-testid={`review-note-block-${path}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              Reviewer note (optional)
            </span>
            <span
              className="text-[10px] text-muted-foreground tabular-nums"
              data-testid={`review-note-count-${path}`}
            >
              {draft.length}/500
            </span>
          </div>
          <Textarea
            value={draft}
            onChange={(event) => onNoteDraftChange(path, event.target.value)}
            rows={2}
            maxLength={500}
            disabled={busy}
            placeholder="Optional context — saved with your review decision"
            className="text-[12.5px] min-h-[40px]"
            data-testid={`review-note-input-${path}`}
          />
          {status !== "Unreviewed" && noteDirty && (
            <Button
              type="button" size="sm" variant="outline"
              disabled={busy}
              onClick={() => onSaveNote(path)}
              data-testid={`review-note-save-btn-${path}`}
            >
              <Check className="w-3.5 h-3.5 mr-1" /> Save note
            </Button>
          )}
        </div>
      )}

      {!writer && status !== "Unreviewed" && savedNote && (
        <div
          className="text-[11.5px] text-muted-foreground whitespace-pre-wrap break-words"
          data-testid={`review-note-display-${path}`}
        >
          <span className="text-[10px] uppercase tracking-[0.16em] mr-2">
            Reviewer note
          </span>
          {savedNote}
        </div>
      )}

      {status !== "Unreviewed" && (review?.reviewed_by_email || review?.reviewed_at) && (
        <div
          className="text-[10.5px] text-muted-foreground"
          data-testid={`review-attribution-${path}`}
        >
          Reviewed by {review.reviewed_by_email || "\u2014"}
          {review.reviewed_at ? ` \u00b7 ${formatDate(review.reviewed_at)}` : ""}
        </div>
      )}
    </li>
  );
}

function StructuredGroup({ heading, groupKey, fields, testid, annotations }) {
  return (
    <div
      className="rounded-md border bg-muted/20 p-4 space-y-3"
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-foreground/80">
        {heading}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map((entry) => {
          const ann = (annotations && entry.canonicalPath)
            ? annotations[entry.canonicalPath]
            : null;
          return (
            <FieldRow
              key={entry.key}
              label={entry.label}
              testid={`bi-structured-${groupKey}-${entry.key}`}
            >
              {entry.kind === "array" ? (
                <ArrayValue
                  values={entry.value}
                  testid={`bi-structured-${groupKey}-${entry.key}-value`}
                  itemPrefix={`${groupKey}-${entry.key}`}
                />
              ) : (
                <StringValue
                  value={entry.value}
                  testid={`bi-structured-${groupKey}-${entry.key}-value`}
                />
              )}
              <FieldEvidence
                annotation={ann}
                testid={`bi-structured-${groupKey}-${entry.key}-evidence`}
              />
            </FieldRow>
          );
        })}
      </div>
    </div>
  );
}

export default function BriefEditor() {
  const { briefId } = useParams();
  const { user } = useAuth();
  const canRead = canAccessBriefIntelligence(user);
  const writer = canWrite(user);

  const [brief, setBrief] = useState(null);
  const [sourceInfo, setSourceInfo] = useState(null);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  // Correction 1 — initialise the first request as loading.
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionBusy, setActionBusy] = useState("");
  // Assignment 2H — human adjudication state, keyed by canonical path.
  const [reviews, setReviews] = useState({});
  const [reviewBusyPath, setReviewBusyPath] = useState("");
  const [editingPath, setEditingPath] = useState("");
  const [editorDraft, setEditorDraft] = useState("");
  // Assignment 2L — optional reviewer-note drafts, keyed by canonical path.
  // Drafts mirror the saved note on load and are sent with every review
  // save; they live only in the human review record, never the extraction.
  const [noteDrafts, setNoteDrafts] = useState({});
  // BI Vocabulary Bridge — normalized Common Taxonomy V1 requirements
  // (derived server-side from the latest Completed extraction) + the
  // deterministic catalog matching results sheet.
  const [normalized, setNormalized] = useState(null);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  // Email integration strike — temporary selection + editable email draft.
  const [selectedTrackIds, setSelectedTrackIds] = useState([]);
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailBusy, setEmailBusy] = useState("");
  const [sendResult, setSendResult] = useState(null);
  // Assignment 2I — Source Viewer state (verification only).
  const [sourceViewOpen, setSourceViewOpen] = useState(false);
  const [sourceViewPath, setSourceViewPath] = useState("");
  const [sourceViewExtractionId, setSourceViewExtractionId] = useState("");
  const [sourceViewLoading, setSourceViewLoading] = useState(false);
  const [sourceViewError, setSourceViewError] = useState("");
  const [sourceView, setSourceView] = useState(null);
  // Assignment 2J — Grounded History Diff state (read-only comparison).
  const [diffSelection, setDiffSelection] = useState([]);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState("");
  const [diffData, setDiffData] = useState(null);
  const [diffFilter, setDiffFilter] = useState("all");

  const load = useCallback(async () => {
    if (!canRead || !briefId) return;
    setLoading(true);
    setLoadError("");
    try {
      const [briefResponse, sourceResponse, latestResponse, historyResponse] = await Promise.all([
        api.get(`/briefs/${briefId}`),
        api.get(`/briefs/${briefId}/source-info`),
        api.get(`/briefs/${briefId}/extractions/latest`),
        api.get(`/briefs/${briefId}/extractions`, { params: { limit: 100 } }),
      ]);
      // Correction 7 — store minimised projections, not raw responses.
      setBrief(projectBriefDetail(briefResponse.data));
      setSourceInfo(projectSourceInfo(sourceResponse.data));
      const projectedLatest = projectExtraction(latestResponse.data);
      setLatest(projectedLatest);
      const rawHistory = Array.isArray(historyResponse.data?.rows)
        ? historyResponse.data.rows
        : [];
      setHistory(rawHistory.map(projectExtraction).filter(Boolean));
      // Assignment 2H — load human adjudication for the latest completed
      // extraction. A review-load failure must never break the page; the
      // panel simply shows the neutral Unreviewed state.
      if (projectedLatest?.id && projectedLatest.status === "Completed") {
        try {
          const reviewsResponse = await api.get(
            `/briefs/${briefId}/extractions/${projectedLatest.id}/field-reviews`,
          );
          const map = {};
          const rows = Array.isArray(reviewsResponse.data?.rows)
            ? reviewsResponse.data.rows : [];
          for (const row of rows) {
            const projected = projectFieldReview(row);
            if (projected?.field_path) map[projected.field_path] = projected;
          }
          setReviews(map);
          // 2L — seed note drafts from the saved review records so the
          // note field reflects persisted state after every reload.
          const drafts = {};
          for (const path of Object.keys(map)) {
            drafts[path] = map[path].reviewer_note || "";
          }
          setNoteDrafts(drafts);
        } catch {
          setReviews({});
          setNoteDrafts({});
        }
      } else {
        setReviews({});
        setNoteDrafts({});
      }
      // BI Vocabulary Bridge — derived, read-only V1 view of the brief's
      // creative requirements. Fail-safe: hidden entirely on any error.
      if (projectedLatest?.id && projectedLatest.status === "Completed") {
        try {
          const normalizedResponse = await api.get(
            `/briefs/${briefId}/normalized-requirements`,
          );
          setNormalized(
            normalizedResponse.data?.available ? normalizedResponse.data : null,
          );
        } catch {
          setNormalized(null);
        }
      } else {
        setNormalized(null);
      }
    } catch (error) {
      setLoadError(safeErrorText(error, "Could not load brief details."));
    } finally {
      setLoading(false);
    }
  }, [briefId, canRead]);

  useEffect(() => {
    if (canRead) load();
  }, [canRead, load]);

  async function createAttempt() {
    setActionBusy("create");
    try {
      const response = await api.post(`/briefs/${briefId}/extractions`);
      toast.success(
        response.data?.version_no != null
          ? `Extraction attempt version ${response.data.version_no} created.`
          : "Extraction attempt created.",
      );
      await load();
    } catch (error) {
      toast.error(safeErrorText(error, "Could not create extraction attempt."));
    } finally {
      setActionBusy("");
    }
  }

  async function runAttempt() {
    if (!latest?.id) return;
    setActionBusy("run");
    try {
      // Assignment 2F — LLM extraction may exceed the shared Axios 30s
      // default. Use a per-request 240s timeout only for this call; the
      // global default is intentionally unchanged.
      const response = await api.post(
        `/briefs/${briefId}/extractions/${latest.id}/run`,
        undefined,
        { timeout: 240000 },
      );
      const status = response.data?.status;
      if (status === "Not Configured") {
        // Correction 11 — keep the terminal message visible long enough
        // to read. The persistent alert below the Run controls carries the
        // authoritative state; this toast is a transient nudge.
        toast.info(
          "Extraction attempt recorded. Automated extraction is not currently configured.",
          { duration: 15000 },
        );
      } else if (status === "Failed") {
        toast.error(
          response.data?.error_safe
            || "The extraction attempt failed safely.",
          { duration: 15000 },
        );
      } else if (status === "Completed") {
        toast.success("Extraction attempt completed.", { duration: 6000 });
      } else {
        toast.success("Extraction attempt run completed.");
      }
      await load();
    } catch (error) {
      toast.error(
        safeErrorText(error, "Could not run extraction attempt."),
        { duration: 15000 },
      );
    } finally {
      setActionBusy("");
    }
  }

  // Assignment 2H — persist one reviewer adjudication. Upserts the separate
  // review record server-side; the machine extraction is never modified and
  // no new extraction version is created by review actions.
  // 2L — the current reviewer-note draft rides along with every save;
  // clearing to Unreviewed clears the active note with the adjudication.
  async function saveReview(fieldPath, reviewStatus, correctedValue, noteOnly) {
    if (!latest?.id) return;
    setReviewBusyPath(fieldPath);
    try {
      const body = { field_path: fieldPath, review_status: reviewStatus };
      if (reviewStatus === "Corrected") body.corrected_value = correctedValue;
      if (reviewStatus !== "Unreviewed") {
        const note = (noteDrafts[fieldPath] || "").trim().slice(0, 500);
        if (note) body.reviewer_note = note;
      }
      const response = await api.put(
        `/briefs/${briefId}/extractions/${latest.id}/field-reviews`,
        body,
      );
      const projected = projectFieldReview(response.data);
      if (projected?.field_path) {
        setReviews((prev) => ({ ...prev, [projected.field_path]: projected }));
        // 2L — resync the draft to what the server actually stored.
        setNoteDrafts((prev) => ({
          ...prev,
          [projected.field_path]: projected.reviewer_note || "",
        }));
      }
      setEditingPath("");
      setEditorDraft("");
      toast.success(
        noteOnly
          ? "Reviewer note saved."
          : reviewStatus === "Unreviewed"
            ? "Review state cleared."
            : `Field marked ${reviewStatus}.`,
      );
    } catch (error) {
      toast.error(safeErrorText(error, "Could not save the field review."));
    } finally {
      setReviewBusyPath("");
    }
  }

  // 2L — update only the note on an already-adjudicated field. Resends the
  // existing review state (and corrected value where applicable) so the
  // single review record is updated in place; no adjudication change.
  async function saveNoteOnly(fieldPath) {
    const existing = reviews[fieldPath];
    if (!existing || existing.review_status === "Unreviewed") return;
    await saveReview(
      fieldPath,
      existing.review_status,
      existing.corrected_value,
      true,
    );
  }

  // BI Vocabulary Bridge — deterministic catalog matching (read-only;
  // eligibility gates enforced server-side and never overridden).
  async function runCatalogMatching() {
    setMatchOpen(true);
    setMatchLoading(true);
    setMatchResult(null);
    setSelectedTrackIds([]);
    setEmailDraft(null);
    setSendResult(null);
    try {
      const response = await api.post(`/briefs/${briefId}/catalog-matches`);
      setMatchResult(response.data || { rows: [], message: "" });
    } catch (error) {
      setMatchResult({
        rows: [],
        message: safeErrorText(
          error, "Catalog matching could not run. Please retry.",
        ),
      });
    } finally {
      setMatchLoading(false);
    }
  }

  // Email integration strike — deterministic draft from selected titles.
  async function prepareSubmissionEmail() {
    if (selectedTrackIds.length === 0) return;
    setEmailBusy("prepare");
    setSendResult(null);
    try {
      const response = await api.post(
        `/briefs/${briefId}/submission-email/prepare`,
        { track_ids: selectedTrackIds },
      );
      const data = response.data || {};
      setEmailDraft({
        to: data.recipient_email || "",
        subject: data.subject || "Music Submission",
        body_text: data.body_text || "",
        flagged: data.flagged || [],
        sendable: !!data.sendable,
      });
    } catch (error) {
      toast.error(safeErrorText(error, "Could not prepare the email."));
    } finally {
      setEmailBusy("");
    }
  }

  // Sends through Kandake's EXISTING Email Delivery. Failure preserves
  // the draft and selection so the operator can safely retry.
  async function sendSubmissionEmail() {
    if (!emailDraft) return;
    setEmailBusy("send");
    try {
      const response = await api.post(
        `/briefs/${briefId}/submission-email/send`,
        {
          track_ids: selectedTrackIds,
          to: emailDraft.to,
          subject: emailDraft.subject,
          body_text: emailDraft.body_text,
        },
      );
      const status = response.data?.status;
      if (status === "sent") {
        setSendResult({ ok: true, message: "Submission email sent." });
        toast.success("Submission email sent.");
      } else {
        setSendResult({
          ok: false,
          message:
            status === "not_configured"
              ? "Email transport is not configured. The draft is preserved."
              : "The email could not be sent. The draft is preserved — you can retry.",
        });
      }
    } catch (error) {
      setSendResult({
        ok: false,
        message: safeErrorText(
          error, "The email could not be sent. The draft is preserved — you can retry.",
        ),
      });
    } finally {
      setEmailBusy("");
    }
  }


  function startCorrect(fieldPath) {
    const canonical = latest?.structured_data?.canonical_values?.[fieldPath];
    const existing = reviews[fieldPath];
    let draft = "";
    if (existing?.review_status === "Corrected" && existing.corrected_value !== null) {
      draft = Array.isArray(existing.corrected_value)
        ? existing.corrected_value.join("\n")
        : correctedValueDisplay(existing.corrected_value);
    } else if (canonical) {
      draft = canonical.kind === "array" && Array.isArray(canonical.raw)
        ? canonical.raw.join("\n")
        : canonical.display;
    }
    setEditorDraft(draft);
    setEditingPath(fieldPath);
  }

  function cancelCorrect() {
    setEditingPath("");
    setEditorDraft("");
  }

  async function saveCorrect(fieldPath) {
    const canonical = latest?.structured_data?.canonical_values?.[fieldPath];
    const kind = canonical?.kind || "string";
    let value;
    if (kind === "array") {
      value = editorDraft
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      if (!value.length) {
        toast.error("Enter at least one corrected value.");
        return;
      }
    } else if (kind === "boolean") {
      if (editorDraft !== "True" && editorDraft !== "False") {
        toast.error("Choose True or False for the corrected value.");
        return;
      }
      value = editorDraft === "True";
    } else if (kind === "number") {
      const parsed = Number(editorDraft);
      if (!editorDraft.trim() || Number.isNaN(parsed)) {
        toast.error("Enter a valid number for the corrected value.");
        return;
      }
      value = parsed;
    } else {
      value = editorDraft.trim();
      if (!value) {
        toast.error("Enter a corrected value before saving.");
        return;
      }
    }
    await saveReview(fieldPath, "Corrected", value);
  }

  // Assignment 2I — open the Source Viewer for one grounded field. Fetches
  // the server-resolved evidence segments; issues no writes of any kind.
  // 2J extension: an explicit extractionId lets the history diff open the
  // viewer for either compared version (defaults to the latest extraction).
  async function openSourceView(fieldPath, extractionId) {
    const targetId = extractionId || latest?.id;
    if (!targetId) return;
    setSourceViewPath(fieldPath);
    setSourceViewExtractionId(targetId);
    setSourceViewOpen(true);
    setSourceViewLoading(true);
    setSourceViewError("");
    setSourceView(null);
    try {
      const response = await api.get(
        `/briefs/${briefId}/extractions/${targetId}/source-view`,
        { params: { field_path: fieldPath } },
      );
      setSourceView(projectSourceView(response.data));
    } catch (error) {
      setSourceViewError(
        safeErrorText(error, "Could not load the parsed source."),
      );
    } finally {
      setSourceViewLoading(false);
    }
  }

  function closeSourceView() {
    setSourceViewOpen(false);
    setSourceViewPath("");
    setSourceViewExtractionId("");
    setSourceView(null);
    setSourceViewError("");
  }

  function stepSourceView(direction, paths) {
    if (!paths.length || !sourceViewPath) return;
    const index = paths.indexOf(sourceViewPath);
    if (index === -1) return;
    const next = (index + direction + paths.length) % paths.length;
    openSourceView(paths[next]);
  }

  // Assignment 2J — read-only comparison of two Completed extractions.
  function toggleDiffSelection(extractionId) {
    setDiffSelection((prev) => {
      if (prev.includes(extractionId)) {
        return prev.filter((id) => id !== extractionId);
      }
      if (prev.length >= 2) return prev;
      return [...prev, extractionId];
    });
  }

  async function openHistoryDiff() {
    if (diffSelection.length !== 2) return;
    setDiffOpen(true);
    setDiffLoading(true);
    setDiffError("");
    setDiffData(null);
    setDiffFilter("all");
    try {
      const response = await api.get(
        `/briefs/${briefId}/extraction-diff`,
        { params: { left: diffSelection[0], right: diffSelection[1] } },
      );
      setDiffData(projectHistoryDiff(response.data));
    } catch (error) {
      setDiffError(safeErrorText(error, "Could not compare the selected extractions."));
    } finally {
      setDiffLoading(false);
    }
  }

  function closeHistoryDiff() {
    setDiffOpen(false);
    setDiffData(null);
    setDiffError("");
  }

  if (!canRead) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="brief-detail-permission-state">
        <Alert>
          <AlertDescription>
            Brief Intelligence is limited to authorised Kandake operators.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading && !brief) {
    return (
      <div
        className="p-6 flex items-center gap-3 text-sm text-muted-foreground"
        data-testid="brief-detail-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading brief…
      </div>
    );
  }

  if (loadError && !brief) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Alert variant="destructive" data-testid="brief-detail-error">
          <AlertDescription>
            {loadError}
            <Button
              variant="link"
              className="h-auto ml-2 p-0"
              onClick={load}
              data-testid="brief-detail-retry"
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const source = sourceInfo || {};
  const latestStatus = latest?.status;
  const canRun = latestStatus === "Pending";
  const terminal = ["Completed", "Failed", "Not Configured"].includes(latestStatus);
  const canCreate = !latest || terminal;
  // Assignment 2H — display-only overlay of adjudicated review states onto
  // the machine annotations (state itself is never mutated).
  const mergedAnnotations = mergeAnnotationsWithReviews(
    latest?.structured_data?.field_annotations,
    reviews,
  );
  const groundedPaths = Object.keys(mergedAnnotations).sort();
  // Assignment 2I — only fields with stored evidence are source-verifiable.
  const evidencePaths = groundedPaths.filter(
    (path) => (latest?.structured_data?.field_annotations?.[path]?.evidence || []).length > 0,
  );
  // Assignment 2J — versions eligible for comparison.
  const completedHistory = history.filter((item) => item.status === "Completed");

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="brief-detail-page">
      <Button asChild variant="ghost" size="sm" data-testid="brief-detail-back">
        <Link to="/advanced/briefs">
          <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to Briefs
        </Link>
      </Button>

      <PageHeader
        title={brief?.title || "Brief Intelligence"}
        subtitle={`MAGI Brief Intelligence \u2022 ${brief?.ref_id || brief?.id || ""}`}
        actions={(
          <Button
            variant="outline"
            onClick={load}
            disabled={loading || Boolean(actionBusy)}
            data-testid="brief-detail-refresh"
          >
            {loading
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Refresh
          </Button>
        )}
      />

      {loadError && (
        <Alert variant="destructive" data-testid="brief-detail-refresh-error">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Brief summary</CardTitle>
          <CardDescription>Safe workflow and record metadata.</CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Read
            label="Reference"
            value={brief?.ref_id || brief?.id}
            mono
            testid="brief-summary-reference"
          />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Workflow status</div>
            <NeutralStatus value={brief?.workflow_status} testid="brief-workflow-status" />
          </div>
          <Read
            label="Source type"
            value={brief?.source?.source_kind}
            testid="brief-summary-source-kind"
          />
          <Read
            label="Created"
            value={formatDate(brief?.created_at)}
            testid="brief-summary-created"
          />
          <Read
            label="Updated"
            value={formatDate(brief?.updated_at)}
            testid="brief-summary-updated"
          />
          <Read
            label="Created by"
            value={brief?.created_by_email}
            testid="brief-summary-created-by"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Source information</CardTitle>
          <CardDescription>
            Metadata only. Private object locations and complete source content
            are intentionally unavailable here.
          </CardDescription>
        </CardHeader>
        <CardContent
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="brief-source-info"
        >
          <Read label="Filename" value={source.filename} testid="brief-source-filename" />
          <Read label="Content type" value={source.content_type} testid="brief-source-content-type" />
          <Read label="Source kind" value={source.source_kind} testid="brief-source-kind" />
          <Read label="Size" value={formatBytes(source.size_bytes)} testid="brief-source-size" />
          <Read label="Uploaded" value={formatDate(source.uploaded_at)} testid="brief-source-uploaded" />
          <Read label="Uploaded by" value={source.uploaded_by_email} testid="brief-source-uploaded-by" />
          <div className="sm:col-span-2 lg:col-span-3">
            <Read
              label="SHA-256 checksum"
              value={source.checksum_sha256}
              mono
              testid="brief-source-checksum"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Safe source preview</CardTitle>
          <CardDescription>
            Bounded preview supplied by the accepted source-info API. This is
            not a complete-source viewer.
          </CardDescription>
        </CardHeader>
        <CardContent data-testid="brief-safe-preview">
          {source.source_text_preview ? (
            <div
              className="text-[13px] whitespace-pre-wrap break-words rounded-md border bg-muted/30 p-4"
              data-testid="brief-safe-preview-content"
            >
              {source.source_text_preview}
            </div>
          ) : (
            <div
              className="text-[13px] text-muted-foreground italic"
              data-testid="brief-safe-preview-empty"
            >
              No safe preview is available.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Extraction controls</CardTitle>
          <CardDescription>
            Attempt creation and execution are separate, auditable operator actions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {latest && (latest.status === "Not Configured" || latest.status === "Failed") && (
            <Alert
              variant={latest.status === "Failed" ? "destructive" : "default"}
              data-testid="brief-controls-persistent-status"
            >
              <AlertDescription>
                <span className="font-semibold">
                  Latest attempt: {latest.status}.
                </span>{" "}
                {latest.status === "Not Configured"
                  ? "Automated extraction is not currently configured. The brief and deterministic source information remain available for human review. Set the extraction provider environment variable to enable automated runs."
                  : (latest.error_safe || "The extraction attempt failed safely.")}
              </AlertDescription>
            </Alert>
          )}
          {!writer ? (
            <Alert data-testid="brief-extraction-readonly">
              <AlertDescription>
                Your role has read-only access to extraction information.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-wrap gap-2">
              {canCreate && (
                <Button
                  onClick={createAttempt}
                  disabled={Boolean(actionBusy)}
                  data-testid="brief-create-attempt"
                >
                  {actionBusy === "create"
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                  {latest ? "Create New Attempt" : "Create Attempt"}
                </Button>
              )}
              {canRun && (
                <Button
                  onClick={runAttempt}
                  disabled={Boolean(actionBusy)}
                  data-testid="brief-run-attempt"
                >
                  {actionBusy === "run"
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <Play className="w-3.5 h-3.5 mr-1.5" />}
                  Run Attempt
                </Button>
              )}
              {latest && !canCreate && !canRun && (
                <span
                  className="text-[13px] text-muted-foreground"
                  data-testid="brief-extraction-waiting-state"
                >
                  Latest attempt status: {latest.status}. Use Refresh to reload its state.
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Latest extraction state</CardTitle>
          <CardDescription>
            Safe provider and lifecycle metadata for the most recent attempt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4" data-testid="brief-latest-extraction">
          {!latest ? (
            <EmptyState
              icon={FileSearch}
              title="No extraction attempts"
              description="Create an attempt when an authorised operator is ready."
            />
          ) : (
            <>
              <ExtractionSummary extraction={latest} />
              {latest.status === "Not Configured" && (
                <Alert data-testid="brief-not-configured-state">
                  <AlertDescription>{NOT_CONFIGURED_MESSAGE}</AlertDescription>
                </Alert>
              )}
              {latest.status === "Failed" && (
                <Alert variant="destructive" data-testid="brief-latest-failed-state">
                  <AlertDescription>
                    {latest.error_safe || "The extraction attempt failed safely."}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {latest && latest.status === "Completed" && latest.structured_data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Structured extraction</CardTitle>
            <CardDescription>
              Deterministic, read-only extraction of the approved brief fields.
              Empty fields indicate that no reliable evidence was detected in
              the source. This is not a recommendation or scoring interface.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4" data-testid="brief-structured-extraction">
            {Array.isArray(latest.warnings) && latest.warnings.length > 0 && (
              <Alert data-testid="brief-structured-warnings">
                <AlertDescription>
                  <div className="font-semibold mb-1">
                    Extraction warnings ({latest.warnings.length})
                  </div>
                  <ul className="list-disc pl-5 text-[12px] space-y-1">
                    {latest.warnings.map((warning, index) => (
                      <li
                        key={index}
                        data-testid={`brief-structured-warning-${index}`}
                      >
                        {warning}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-3 text-[11.5px] text-muted-foreground">
              <span data-testid="brief-structured-provider">
                Provider: {latest.provider_name || "\u2014"}
              </span>
              <span data-testid="brief-structured-provider-model">
                Model: {latest.provider_model || "\u2014"}
              </span>
              <span data-testid="brief-structured-completed">
                Extracted at: {formatDate(latest.completed_at)}
              </span>
            </div>
            {(() => {
              const anns = latest.structured_data.field_annotations || {};
              const groundedCount = Object.keys(anns).length;
              let evidenceCount = 0;
              for (const p of Object.keys(anns)) {
                const a = anns[p];
                if (a && Array.isArray(a.evidence)) {
                  evidenceCount += a.evidence.length;
                }
              }
              const omittedCount = Array.isArray(latest.missing_fields)
                ? latest.missing_fields.length : 0;
              return (
                <div
                  className="rounded-md border border-dashed border-border/70 bg-muted/30 px-3 py-2 text-[12px] text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1"
                  data-testid="brief-structured-grounding-summary"
                >
                  <span data-testid="brief-structured-grounded-count">
                    Grounded fields: <span className="font-semibold text-foreground/80">{groundedCount}</span>
                  </span>
                  <span data-testid="brief-structured-evidence-count">
                    Verified evidence items: <span className="font-semibold text-foreground/80">{evidenceCount}</span>
                  </span>
                  <span data-testid="brief-structured-omitted-count">
                    Omitted for missing evidence: <span className="font-semibold text-foreground/80">{omittedCount}</span>
                  </span>
                  <span className="italic">
                    AI extraction still requires human review.
                  </span>
                </div>
              );
            })()}

            <StructuredGroup
              heading="Core Brief Information"
              groupKey="core"
              testid="brief-structured-core"
              annotations={mergedAnnotations}
              fields={[
                { key: "brief_title",       label: "Brief title",       value: latest.structured_data.core.brief_title,       canonicalPath: "source_information.brief_title" },
                { key: "client_or_company", label: "Client / company",  value: latest.structured_data.core.client_or_company, canonicalPath: "source_information.client_or_buyer_name" },
                { key: "project_name",      label: "Project name",      value: latest.structured_data.core.project_name,      canonicalPath: "opportunity_details.project_name" },
                { key: "brief_type",        label: "Brief type",        value: latest.structured_data.core.brief_type,        canonicalPath: "opportunity_details.project_type" },
                { key: "summary",           label: "Summary",           value: latest.structured_data.core.summary,           canonicalPath: "opportunity_details.summary" },
              ]}
            />

            <StructuredGroup
              heading="Creative Direction"
              groupKey="creative"
              testid="brief-structured-creative"
              fields={[
                { key: "genres",                label: "Genres",                value: latest.structured_data.creative.genres,                kind: "array" },
                { key: "moods",                 label: "Moods",                 value: latest.structured_data.creative.moods,                 kind: "array" },
                { key: "themes",                label: "Themes",                value: latest.structured_data.creative.themes },
                { key: "reference_artists",     label: "Reference artists",     value: latest.structured_data.creative.reference_artists,     kind: "array" },
                { key: "reference_tracks",      label: "Reference tracks",      value: latest.structured_data.creative.reference_tracks,      kind: "array" },
                { key: "instrumentation",       label: "Instrumentation",       value: latest.structured_data.creative.instrumentation,       kind: "array" },
                { key: "vocal_requirements",    label: "Vocal requirements",    value: latest.structured_data.creative.vocal_requirements },
                { key: "language_requirements", label: "Language requirements", value: latest.structured_data.creative.language_requirements, kind: "array" },
                { key: "tempo_or_bpm",          label: "Tempo / BPM",           value: latest.structured_data.creative.tempo_or_bpm },
                { key: "era_or_style",          label: "Era / style",           value: latest.structured_data.creative.era_or_style },
              ]}
            />

            <StructuredGroup
              heading="Usage and Placement"
              groupKey="usage"
              testid="brief-structured-usage"
              fields={[
                { key: "media_type",              label: "Media type",             value: latest.structured_data.usage.media_type },
                { key: "usage_description",       label: "Usage description",      value: latest.structured_data.usage.usage_description },
                { key: "territory",               label: "Territory",              value: latest.structured_data.usage.territory, kind: "array" },
                { key: "term",                    label: "Term",                   value: latest.structured_data.usage.term },
                { key: "exclusivity",             label: "Exclusivity",            value: latest.structured_data.usage.exclusivity },
                { key: "edit_or_cut_requirements",label: "Edit / cut requirements",value: latest.structured_data.usage.edit_or_cut_requirements },
              ]}
            />

            <StructuredGroup
              heading="Business and Timing"
              groupKey="business"
              testid="brief-structured-business"
              fields={[
                { key: "budget",              label: "Budget",              value: latest.structured_data.business.budget },
                { key: "currency",            label: "Currency",            value: latest.structured_data.business.currency },
                { key: "submission_deadline", label: "Submission deadline", value: latest.structured_data.business.submission_deadline },
                { key: "project_timeline",    label: "Project timeline",    value: latest.structured_data.business.project_timeline },
                { key: "contact_name",        label: "Contact name",        value: latest.structured_data.business.contact_name },
                { key: "contact_email",       label: "Contact email",       value: latest.structured_data.business.contact_email },
              ]}
            />

            <StructuredGroup
              heading="Restrictions and Notes"
              groupKey="restrictions"
              testid="brief-structured-restrictions"
              fields={[
                { key: "lyrical_restrictions",  label: "Lyrical restrictions",  value: latest.structured_data.restrictions.lyrical_restrictions },
                { key: "content_restrictions",  label: "Content restrictions",  value: latest.structured_data.restrictions.content_restrictions },
                { key: "rights_requirements",   label: "Rights requirements",   value: latest.structured_data.restrictions.rights_requirements },
                { key: "delivery_requirements", label: "Delivery requirements", value: latest.structured_data.restrictions.delivery_requirements, kind: "array" },
                { key: "additional_notes",      label: "Additional notes",      value: latest.structured_data.restrictions.additional_notes },
              ]}
            />
          </CardContent>
        </Card>
      )}

      {/* BI Vocabulary Bridge — normalized Common Taxonomy V1 view. Shows
          only the canonical values genuinely supported by the brief. */}
      {normalized?.profile && (normalized.populated_fields || []).length > 0 && (
        <Card data-testid="brief-normalized-requirements">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ListMusic className="w-4 h-4" /> Normalized Requirements
            </CardTitle>
            <CardDescription>
              What Kandake believes this brief is asking for, in Common
              Taxonomy V1 terms. Derived from the extraction — unknown
              values stay blank and are never fabricated.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px]">
              {[
                ["genre", "Genre"], ["mood", "Mood"], ["tempo", "Tempo"],
                ["vocal_type", "Vocal Type"], ["instrumentation", "Instrumentation"],
                ["best_use", "Best Use"], ["themes", "Themes"],
                ["era_style", "Era / Style"], ["media_type", "Media Type"],
              ].map(([key, label]) => {
                const value = normalized.profile[key];
                const text = Array.isArray(value) ? value.join(", ") : value;
                if (!text) return null;
                return (
                  <div key={key} className="flex gap-2" data-testid={`brief-normalized-${key}`}>
                    <span className="text-muted-foreground min-w-[110px]">{label}</span>
                    <span className="font-medium">{text}</span>
                  </div>
                );
              })}
            </div>
            <Button
              size="sm"
              onClick={runCatalogMatching}
              disabled={matchLoading}
              data-testid="brief-find-matches-btn"
            >
              {matchLoading
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Target className="w-4 h-4 mr-1" />}
              Find Catalog Matches
            </Button>
          </CardContent>
        </Card>
      )}

      {latest && latest.status === "Completed" && latest.structured_data
        && groundedPaths.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> Reviewer field adjudication
            </CardTitle>
            <CardDescription>
              Confirm, correct, reject, or flag each grounded field. Reviewer
              decisions are stored separately from the machine extraction —
              original extracted values, evidence, and provenance are always
              preserved, and no new extraction is created by review actions.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="brief-field-review-panel">
            {!writer && (
              <Alert data-testid="brief-field-review-readonly">
                <AlertDescription>
                  Your role has read-only access to reviewer adjudication.
                </AlertDescription>
              </Alert>
            )}
            <div
              className="text-[11.5px] text-muted-foreground"
              data-testid="brief-field-review-summary"
            >
              {(() => {
                const adjudicated = groundedPaths.filter(
                  (p) => reviews[p] && reviews[p].review_status !== "Unreviewed",
                ).length;
                return `${adjudicated} of ${groundedPaths.length} grounded fields adjudicated.`;
              })()}
            </div>
            <ul className="divide-y border rounded-md" data-testid="brief-field-review-list">
              {groundedPaths.map((path) => (
                <FieldReviewRow
                  key={path}
                  path={path}
                  annotation={latest.structured_data.field_annotations[path]}
                  canonicalValue={latest.structured_data.canonical_values?.[path]}
                  review={reviews[path]}
                  writer={writer}
                  busyPath={reviewBusyPath}
                  editingPath={editingPath}
                  editorDraft={editorDraft}
                  setEditorDraft={setEditorDraft}
                  onAction={(fieldPath, status) => saveReview(fieldPath, status)}
                  onStartCorrect={startCorrect}
                  onSaveCorrect={saveCorrect}
                  onCancelCorrect={cancelCorrect}
                  onViewSource={openSourceView}
                  noteDraft={noteDrafts[path] || ""}
                  onNoteDraftChange={(fieldPath, value) =>
                    setNoteDrafts((prev) => ({ ...prev, [fieldPath]: value }))
                  }
                  onSaveNote={saveNoteOnly}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <SourceViewerSheet
        open={sourceViewOpen}
        onClose={closeSourceView}
        loading={sourceViewLoading}
        error={sourceViewError}
        view={sourceView}
        fieldLabel={sourceViewPath ? reviewFieldLabel(sourceViewPath) : ""}
        onPrev={() => stepSourceView(-1, evidencePaths)}
        onNext={() => stepSourceView(1, evidencePaths)}
        hasNav={evidencePaths.length > 1 && sourceViewExtractionId === latest?.id}
      />

      <HistoryDiffDialog
        open={diffOpen}
        onClose={closeHistoryDiff}
        loading={diffLoading}
        error={diffError}
        diff={diffData}
        filter={diffFilter}
        setFilter={setDiffFilter}
        onViewSource={openSourceView}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HistoryIcon className="w-4 h-4" /> Extraction version history
          </CardTitle>
          <CardDescription>Newest version first.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {history.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={diffSelection.length !== 2}
                onClick={openHistoryDiff}
                data-testid="history-diff-compare-btn"
              >
                <GitCompare className="w-3.5 h-3.5 mr-1.5" />
                Compare selected ({diffSelection.length}/2)
              </Button>
              {completedHistory.length < 2 ? (
                <span
                  className="text-[11.5px] italic text-muted-foreground"
                  data-testid="history-diff-insufficient"
                >
                  At least two completed extractions are required for comparison.
                </span>
              ) : (
                <span className="text-[11.5px] text-muted-foreground">
                  Select two completed versions to compare field by field.
                </span>
              )}
            </div>
          )}
          {history.length === 0 ? (
            <div
              className="text-[13px] text-muted-foreground italic"
              data-testid="brief-history-empty"
            >
              No extraction history is available.
            </div>
          ) : (
            <ul className="divide-y border rounded-md" data-testid="brief-extraction-history">
              {history.map((item) => (
                <li key={item.id} className="p-3">
                  {item.status === "Completed" && (
                    <label
                      className="flex items-center gap-2 mb-1.5 cursor-pointer w-fit"
                      data-testid={`history-diff-select-label-${item.id}`}
                    >
                      <Checkbox
                        checked={diffSelection.includes(item.id)}
                        disabled={
                          !diffSelection.includes(item.id) && diffSelection.length >= 2
                        }
                        onCheckedChange={() => toggleDiffSelection(item.id)}
                        data-testid={`history-diff-select-${item.id}`}
                      />
                      <span className="text-[11px] text-muted-foreground">
                        Select for comparison
                      </span>
                    </label>
                  )}
                  <ExtractionSummary extraction={item} prefix={`history-${item.id}`} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* BI Vocabulary Bridge — catalog match results (read-only). */}
      <Sheet open={matchOpen} onOpenChange={setMatchOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto"
          data-testid="brief-match-sheet"
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Target className="w-4 h-4" /> Catalog Matches
            </SheetTitle>
            <SheetDescription>
              Deterministic weighted matching against eligible Catalog
              Intelligence profiles. Existing rights, clearance, and
              eligibility rules are enforced and never overridden.
            </SheetDescription>
          </SheetHeader>
          <div className="py-4 space-y-3">
            {matchLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Matching…
              </div>
            ) : !matchResult || matchResult.rows.length === 0 ? (
              <div
                className="text-sm text-muted-foreground border border-dashed rounded-md px-4 py-6 text-center"
                data-testid="brief-match-empty"
              >
                {matchResult?.message || "No strong eligible catalog matches found."}
              </div>
            ) : (
              <>
                <p className="text-[11.5px] text-muted-foreground" data-testid="brief-match-summary">
                  {matchResult.rows.length} candidate(s) from an eligible pool
                  of {matchResult.eligible_pool}, strongest first.
                  {writer ? " Select titles to prepare a submission email." : ""}
                </p>
                <ul className="space-y-2">
                  {matchResult.rows.map((row, index) => (
                    <li
                      key={row.track_id}
                      className="rounded-md border border-border p-3 space-y-2"
                      data-testid={`brief-match-row-${row.track_id}`}
                    >
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex items-start gap-2 min-w-0">
                          {writer && (
                            <Checkbox
                              className="mt-0.5"
                              checked={selectedTrackIds.includes(row.track_id)}
                              onCheckedChange={(checked) => {
                                setEmailDraft(null);
                                setSendResult(null);
                                setSelectedTrackIds((prev) =>
                                  checked
                                    ? [...prev, row.track_id]
                                    : prev.filter((id) => id !== row.track_id),
                                );
                              }}
                              data-testid={`brief-match-select-${row.track_id}`}
                            />
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {index + 1}. {row.title || "Untitled"}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {row.artist || "\u2014"}
                              {row.catalog_track_id ? ` · ${row.catalog_track_id}` : ""}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge data-testid={`brief-match-score-${row.track_id}`}>
                            {row.score}% Match
                          </Badge>
                          <Badge variant="outline" className="text-[10px]" data-testid={`brief-match-clearance-${row.track_id}`}>
                            {row.clearance_status || "\u2014"}
                          </Badge>
                        </div>
                      </div>
                      <div className="space-y-0.5 text-[11.5px]" data-testid={`brief-match-reasons-${row.track_id}`}>
                        {(row.matched_fields || []).map((entry) => (
                          <div key={entry.field} className="flex gap-2">
                            <span className="text-muted-foreground min-w-[100px] capitalize">
                              {entry.field.replace("_", " ")}
                            </span>
                            <span>{(entry.values || []).join(", ")}</span>
                          </div>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Email integration — selection → deterministic draft →
                    review/edit → send via EXISTING Email Delivery. */}
                {writer && (
                  <div className="space-y-3 pt-1" data-testid="brief-email-panel">
                    {!emailDraft && (
                      <Button
                        size="sm"
                        onClick={prepareSubmissionEmail}
                        disabled={selectedTrackIds.length === 0 || !!emailBusy}
                        data-testid="brief-prepare-email-btn"
                      >
                        {emailBusy === "prepare"
                          ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          : <Send className="w-4 h-4 mr-1" />}
                        Prepare Email
                        {selectedTrackIds.length > 0
                          ? ` (${selectedTrackIds.length})` : ""}
                      </Button>
                    )}

                    {emailDraft && (
                      <div className="rounded-md border border-border p-3 space-y-3">
                        <div className="text-sm font-medium">Submission Email</div>
                        {emailDraft.flagged.length > 0 && (
                          <Alert variant="destructive" data-testid="brief-email-flagged">
                            <AlertDescription className="text-xs">
                              Titles needing attention before sending:{" "}
                              {emailDraft.flagged
                                .map((row) => `${row.title || row.track_id} — ${row.issue}`)
                                .join(" | ")}
                              {" "}Deselect them to continue.
                            </AlertDescription>
                          </Alert>
                        )}
                        <div className="space-y-1">
                          <Label className="text-xs" htmlFor="brief-email-to">Recipient</Label>
                          <Input
                            id="brief-email-to"
                            type="email"
                            placeholder="recipient@example.com"
                            value={emailDraft.to}
                            onChange={(e) =>
                              setEmailDraft((p) => ({ ...p, to: e.target.value }))
                            }
                            data-testid="brief-email-to"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs" htmlFor="brief-email-subject">Subject</Label>
                          <Input
                            id="brief-email-subject"
                            value={emailDraft.subject}
                            onChange={(e) =>
                              setEmailDraft((p) => ({ ...p, subject: e.target.value }))
                            }
                            data-testid="brief-email-subject"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs" htmlFor="brief-email-body">Body</Label>
                          <Textarea
                            id="brief-email-body"
                            rows={10}
                            className="text-[12.5px] font-normal"
                            value={emailDraft.body_text}
                            onChange={(e) =>
                              setEmailDraft((p) => ({ ...p, body_text: e.target.value }))
                            }
                            data-testid="brief-email-body"
                          />
                        </div>
                        {sendResult && (
                          <Alert
                            variant={sendResult.ok ? "default" : "destructive"}
                            data-testid="brief-email-send-result"
                          >
                            <AlertDescription className="text-xs">
                              {sendResult.message}
                            </AlertDescription>
                          </Alert>
                        )}
                        {!sendResult?.ok && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={sendSubmissionEmail}
                              disabled={
                                !!emailBusy
                                || !emailDraft.sendable
                                || !emailDraft.to.trim()
                                || !emailDraft.subject.trim()
                                || !emailDraft.body_text.trim()
                              }
                              data-testid="brief-email-send-btn"
                            >
                              {emailBusy === "send"
                                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                : <Send className="w-4 h-4 mr-1" />}
                              Send
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setEmailDraft(null); setSendResult(null); }}
                              disabled={!!emailBusy}
                              data-testid="brief-email-discard-btn"
                            >
                              Discard draft
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
